import { Controller } from "@hotwired/stimulus"

// Multi-step chrome for design-system Course Builder create flow (Details -> Educators).
export default class extends Controller {
  static targets = ["panel", "step", "nextButton", "submitButton"]
  static values = { step: { type: String, default: "details" } }

  connect() {
    this.boundValidate = this.validateCurrentStep.bind(this)
    this.element.addEventListener("input", this.boundValidate)
    this.element.addEventListener("change", this.boundValidate)
    this.showStep(this.stepValue)
    this.validateCurrentStep()
  }

  disconnect() {
    this.element.removeEventListener("input", this.boundValidate)
    this.element.removeEventListener("change", this.boundValidate)
  }

  goToEducators(event) {
    event?.preventDefault()
    if (!this.validateStep("details")) return
    this.showStep("educators")
  }

  showStep(step) {
    this.stepValue = step
    const currentIndex = this.stepIndex(step)

    this.panelTargets.forEach((panel) => {
      const active = panel.dataset.step === step
      panel.classList.toggle("hidden", !active)
      panel.classList.toggle("flex", active)
    })

    this.stepTargets.forEach((el) => {
      const index = Number(el.dataset.stepIndex)
      const state = index < currentIndex ? "complete" : index === currentIndex ? "current" : "upcoming"
      const circle = el.querySelector('[data-course-builder-wizard-target="stepCircle"]')
      const label = el.querySelector('[data-course-builder-wizard-target="stepLabel"]')
      const number = el.querySelector('[data-course-builder-wizard-target="stepNumber"]')
      const check = el.querySelector('[data-course-builder-wizard-target="stepCheck"]')

      if (state === "current") {
        el.setAttribute("aria-current", "step")
      } else {
        el.removeAttribute("aria-current")
      }

      if (circle) circle.dataset.state = state
      if (label) label.dataset.state = state
      if (number) number.classList.toggle("hidden", state === "complete")
      if (check) check.classList.toggle("hidden", state !== "complete")
    })

    this.validateCurrentStep()
  }

  stepIndex(step) {
    const match = this.stepTargets.find((el) => el.dataset.step === step)
    return match ? Number(match.dataset.stepIndex) : 0
  }

  validateCurrentStep() {
    this.validateStep(this.stepValue)
  }

  validateStep(step) {
    const panel = this.panelTargets.find((el) => el.dataset.step === step)
    if (!panel) return false

    const selectors = {
      details: [
        "input[name='document_set[name]']",
        "input[name='document_set[start_date]']",
        "input[name='document_set[end_date]']"
      ].join(", "),
      educators: "input[name='document_set[responsible_person_id]'], select[name='document_set[responsible_person_id]']"
    }

    const fields = panel.querySelectorAll(selectors[step] || "")
    let valid = Array.from(fields).every((field) => (field.value || "").toString().trim().length > 0)

    if (step === "details" && valid) {
      valid = this.detailsDatesValid(panel)
    }

    if (step === "details" && this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = !valid
    }

    if (step === "educators" && this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !valid
    }

    return valid
  }

  detailsDatesValid(panel) {
    const startInput = panel.querySelector("input[name='document_set[start_date]']")
    const endInput = panel.querySelector("input[name='document_set[end_date]']")
    const startDate = this.parseDate(startInput?.value)
    const endDate = this.parseDate(endInput?.value)

    if (!startDate || !endDate) return false

    return endDate.getTime() > startDate.getTime()
  }

  parseDate(value) {
    const match = String(value || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (!match) return null

    const month = Number(match[1])
    const day = Number(match[2])
    const year = Number(match[3])
    const date = new Date(year, month - 1, day)

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null
    }

    return date
  }
}
